import styles from './index.module.css'

import cn from 'clsx'
import type { ComponentProps } from 'react'
import React from 'react'

import { ArrowDropDownIcon } from '../../ui/icon/svg-icons'
import { Button, DropdownMenu, Flex } from '../index'

export interface SplitButtonDropdownItem {
  label: string
  onSelect: () => void
  disabled?: boolean
}

export interface SplitButtonProps {
  /** Text to display on the main button */
  caption: string
  /** Icon to display on the main button */
  iconNode?: React.ReactNode
  /** Size of the button - matches Radix UI Button sizes */
  size?: ComponentProps<typeof Button>['size']
  /** Variant of the button - matches Radix UI Button variants */
  variant?: ComponentProps<typeof Button>['variant']
  /** Color of the button - matches Radix UI Button colors */
  color?: ComponentProps<typeof Button>['color']
  /** Handler for main button click */
  onClick: () => void
  /** Array of dropdown menu items */
  dropdownItems: SplitButtonDropdownItem[]
  /** Whether the button is disabled */
  disabled?: boolean
  /** Additional CSS class name */
  className?: string
}
/**
 * SplitButton component
 *
 * A compound button that combines a primary action with a dropdown of additional actions.
 * The main button triggers the primary action, while the adjacent dropdown button reveals
 * a menu of secondary actions.
 *
 * This component can be used as a direct replacement for {@link EllipsisMenu} when the
 * intended behavior is to use `behavior="button-dropdown"`. It provides a more explicit
 * and accessible split-button UI, while maintaining compatibility with the same dropdown
 * menu item structure.
 *
 * @example
 * <SplitButton
 *   caption="Save"
 *   onClick={handleSave}
 *   dropdownItems={[
 *     { label: 'Save as draft', onSelect: handleSaveDraft },
 *     { label: 'Save & publish', onSelect: handlePublish }
 *   ]}
 * />
 *
 * @see EllipsisMenu
 */

export const SplitButton: React.FC<SplitButtonProps> = ({
  caption,
  iconNode,
  size = '2',
  variant = 'solid',
  color,
  onClick,
  dropdownItems,
  disabled = false,
  className,
}) => {
  const dropdownButton = (
    <Button
      variant={variant}
      size={size}
      color={color}
      disabled={disabled}
      className={cn(
        styles.dropdownButton,
        styles[
          `dropdownButton${variant.charAt(0).toUpperCase() + variant.slice(1)}`
        ],
        size === '1' ? styles.dropdownButton1 : styles.dropdownButton2,
        { [styles.ghostVariant]: variant === 'ghost' },
      )}
    >
      <ArrowDropDownIcon size={size === '1' ? 14 : 18} />
    </Button>
  )

  return (
    <Flex
      align="center"
      className={cn(className, { [styles.disabled]: disabled })}
    >
      <Button
        variant={variant}
        size={size}
        color={color}
        onClick={onClick}
        disabled={disabled}
        className={cn(styles.mainButton, {
          [styles.ghostVariant]: variant === 'ghost',
        })}
      >
        {iconNode}
        {caption}
      </Button>

      {disabled ? (
        dropdownButton
      ) : (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>{dropdownButton}</DropdownMenu.Trigger>

          <DropdownMenu.Content
            variant="soft"
            align="end"
            size={size === '1' ? '1' : '2'}
            color={color}
          >
            {dropdownItems.map((item, index) => (
              <DropdownMenu.Item
                key={index}
                onSelect={item.onSelect}
                disabled={item.disabled}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
    </Flex>
  )
}

export default SplitButton
